import { useCallback, useEffect, useMemo, useState } from "react";
import { Chip } from "@mui/material";
import Icon from "@mui/material/Icon";
import { GridColDef } from "@mui/x-data-grid";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

import { LojaPageContainer, LojaDataGrid } from "layouts/pages/loja/common";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { setMessage, useMaterialUIController } from "context";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { formatMoneyBR } from "helpers/formatMoney";
import { formatDateEndOfDay } from "helpers/formatDate";
import {
  ISolicitacaoOrcamento,
  ORIGEM_SOLICITACAO_LABEL,
  STATUS_SOLICITACAO_LABEL,
  StatusSolicitacaoOrcamento,
} from "interfaces/orcamento";
import { listarSolicitacoesOrcamento } from "services/orcamentoSolicitacoes";
import SolicitacaoDetalheDrawer from "./components/SolicitacaoDetalheDrawer";
import NovaSolicitacaoOrcamentoModal from "./components/NovaSolicitacaoOrcamentoModal";
import SolicitacoesOrcamentoResumoCards, {
  type SolicitacoesOrcamentoResumo,
} from "./components/SolicitacoesOrcamentoResumoCards";
import SolicitacoesFilters from "./components/SolicitacoesFilters";
import { ORCAMENTO_ACCENT } from "./components/orcamento-solicitacoes.theme";

const SEARCH_DEBOUNCE_MS = 400;

function statusColor(status: StatusSolicitacaoOrcamento) {
  switch (status) {
    case "NOVO":
      return "warning";
    case "EM_ANALISE":
      return "info";
    case "PROPOSTA_ENVIADA":
      return "primary";
    case "APROVADO":
      return "success";
    case "RECUSADO":
    case "CANCELADO":
      return "error";
    default:
      return "default";
  }
}

export default function OrcamentoSolicitacoes() {
  const [, dispatch] = useMaterialUIController();
  const [rows, setRows] = useState<ISolicitacaoOrcamento[]>([]);
  const [resumoRows, setResumoRows] = useState<ISolicitacaoOrcamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"" | StatusSolicitacaoOrcamento>("");
  const [busca, setBusca] = useState("");
  const debouncedBusca = useDebouncedValue(busca, SEARCH_DEBOUNCE_MS);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lista, todas] = await Promise.all([
        listarSolicitacoesOrcamento({
          status: statusFilter || undefined,
          busca: debouncedBusca.trim() || undefined,
        }),
        listarSolicitacoesOrcamento(),
      ]);
      setRows(lista);
      setResumoRows(todas);
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text:
          error instanceof Error
            ? error.message
            : "Falha ao carregar solicitações.",
      });
    } finally {
      setLoading(false);
    }
  }, [dispatch, statusFilter, debouncedBusca]);

  useEffect(() => {
    load();
  }, [load]);

  const resumo = useMemo((): SolicitacoesOrcamentoResumo => {
    const counts: SolicitacoesOrcamentoResumo = {
      TOTAL: resumoRows.length,
      NOVO: 0,
      EM_ANALISE: 0,
      AGUARDANDO_CLIENTE: 0,
      PROPOSTA_ENVIADA: 0,
      APROVADO: 0,
      RECUSADO: 0,
      CANCELADO: 0,
    };
    for (const row of resumoRows) {
      counts[row.status] += 1;
    }
    return counts;
  }, [resumoRows]);

  const isFiltered = statusFilter !== "" || debouncedBusca.trim() !== "";

  const columns: GridColDef<ISolicitacaoOrcamento>[] = useMemo(
    () => [
      {
        field: "status",
        headerName: "Status",
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            label={STATUS_SOLICITACAO_LABEL[params.row.status]}
            color={statusColor(params.row.status) as "default"}
          />
        ),
      },
      {
        field: "origem",
        headerName: "Origem",
        width: 100,
        renderCell: (params) => {
          const origem = params.row.origem ?? "SITE";
          return (
            <Chip
              size="small"
              variant="outlined"
              label={ORIGEM_SOLICITACAO_LABEL[origem]}
              color={origem === "INTERNO" ? "primary" : "default"}
            />
          );
        },
      },
      { field: "codigo", headerName: "Código", width: 140 },
      {
        field: "createAt",
        headerName: "Recebida",
        width: 110,
        valueGetter: (params) => formatDateEndOfDay(params.row.createAt ?? ""),
      },
      {
        field: "dataEvento",
        headerName: "Evento",
        width: 110,
        valueGetter: (params) => formatDateEndOfDay(params.row.dataEvento),
      },
      {
        field: "nomeOrganizador",
        headerName: "Organizador",
        flex: 1,
        minWidth: 140,
      },
      { field: "cidade", headerName: "Cidade", width: 120 },
      { field: "email", headerName: "E-mail", flex: 1, minWidth: 180 },
      {
        field: "valorCalculado",
        headerName: "Calculado",
        width: 110,
        valueFormatter: (params) => formatMoneyBR(params.value),
      },
      {
        field: "valorProposto",
        headerName: "Proposta",
        width: 110,
        valueFormatter: (params) =>
          params.value != null ? formatMoneyBR(params.value) : "—",
      },
    ],
    []
  );

  const handleRowClick = (id: number) => {
    setSelectedId(id);
    setDrawerOpen(true);
  };

  const handleCreated = async (id: number, codigo: string) => {
    setMessage(dispatch, {
      open: true,
      severity: "success",
      text: `Solicitação ${codigo} criada. Ajuste a precificação e envie a proposta ao cliente.`,
    });
    await load();
    setSelectedId(id);
    setDrawerOpen(true);
  };

  return (
    <LojaPageContainer
      title="Orçamento · Solicitações"
      subtitle="Gerencie pedidos do site e orçamentos criados pela equipe no painel."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          onClick={() => setCreateOpen(true)}
          sx={{
            background: `linear-gradient(195deg, ${ORCAMENTO_ACCENT}, #e04a1a)`,
          }}
        >
          <Icon sx={{ mr: 0.5 }}>add</Icon>
          Nova solicitação
        </MDButton>
      }
    >
      <SolicitacoesOrcamentoResumoCards
        resumo={resumo}
        exibindo={rows.length}
        isFiltered={isFiltered}
      />

      <SolicitacoesFilters
        busca={busca}
        statusFilter={statusFilter}
        totalExibindo={rows.length}
        onBuscaChange={setBusca}
        onStatusChange={setStatusFilter}
      />

      {!loading && rows.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <InboxOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhuma solicitação encontrada
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {isFiltered
              ? "Ajuste a busca ou limpe os filtros de status."
              : "Ainda não há solicitações. Crie uma pelo painel ou aguarde pedidos do site."}
          </MDTypography>
        </MDBox>
      ) : (
        <LojaDataGrid
          rows={rows}
          getRowId={(r) => r.id}
          columns={columns}
          loading={loading}
          onRowClick={(params) => handleRowClick(params.row.id)}
          initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
          sx={{
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-row:hover": {
              bgcolor: "rgba(249, 96, 48, 0.04)",
            },
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "grey.100",
              fontWeight: 700,
            },
          }}
        />
      )}

      <NovaSolicitacaoOrcamentoModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <SolicitacaoDetalheDrawer
        open={drawerOpen}
        solicitacaoId={selectedId}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedId(null);
        }}
        onUpdated={load}
      />
    </LojaPageContainer>
  );
}
